import React, { useContext, useEffect } from 'react';
import fetchRetry from '../../utils/fetchRetry';
import { getBrowserErrorMessage } from '../../utils/browserUtils';
import getEnvConfig from '../../utils/config';
import { getIdToken } from '../../auth/AuthenticationContext';
import { getPuFrontendLogsUrl } from '../../utils/urlUtils';
import { resolveUserVersion } from '../../api/userVersion';
import { MonitoringContext } from '../../utils/monitoring-context/useMonitoring';
import { v4 } from 'uuid';
import { createXbLogger } from '../../utils/xb-monitoring';
import { GAME_XB_COMPONENT } from './constants';

type WebGLProps = {
    webGLConfig?: string;
};

type WebGlLoadFailedStage =
    | 'config'
    | 'config_network'
    | 'loader'
    | 'canvas_missing'
    | 'no_unity_loader'
    | 'unity_load';

// loader, unity_load and config_network often have a client-side cause (network, ad blocker, memory, GPU),
// so they go to RUM only.
const XB_PAGE_FAILURE_STAGES: readonly WebGlLoadFailedStage[] = ['config', 'canvas_missing', 'no_unity_loader'];

// "/webgl/<build>/MyLoader.js" -> "<build>"; keeps nested paths such as "cloud-quest-public-demo/2023...".
const buildFromLoaderUrl = (loaderUrl?: string): string | undefined => loaderUrl?.match(/\/webgl\/(.+)\/[^/]+$/)?.[1];

const WebGL = ({ webGLConfig }: WebGLProps) => {
    var monitoringContext = useContext(MonitoringContext);

    let sessionId: string | undefined = undefined;

    if (monitoringContext.sessionId) {
        console.log('Re-using RUM session Id');
        sessionId = monitoringContext.sessionId;
    } else {
        console.log('RUM Not configured. Will generate new Session Id');
        sessionId = v4();
    }

    window.pu_environment.session_id = sessionId;

    const notifyLoadEvent = async () => {
        const frontedLogsUrl = getPuFrontendLogsUrl();

        if (frontedLogsUrl) {
            const token = getIdToken();

            // Temporary hack: include frontend_version to segregate metrics for v1 vs v2.0+ users
            let isV2 = false;
            if (getEnvConfig().webgl_game_id === 'cloud-quest') {
                try {
                    const version = await resolveUserVersion(token);
                    isV2 = version !== 'V1';
                } catch (e) { /* ignore - best effort */ }
            }

            const event = {
                player_id: window.pu_environment.principal_id,
                session_id: sessionId,
                log_type: 'Datapoint',
                event_type: 'pre_webgl_load',
                time_stamp_unix: Math.floor(Date.now() / 1000),
                data: {
                    metric_name: 'pre_webgl_load',
                    ...(isV2 && { frontend_version: '0.0.2001' }),
                },
            };

            const records = {
                records: [JSON.stringify(event)],
            };

            const headers = new Headers();
            headers.append('Authorization', token);
            headers.append('Content-Type', 'application/json');

            var frontendLogsPayload = {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(records),
            };

            return fetchRetry(frontedLogsUrl, frontendLogsPayload);
        }
        return Promise.resolve();
    };

    const notifyWebGlFailedLoadEvent = (err: Error) => {
        const frontedLogsUrl = getPuFrontendLogsUrl();

        if (frontedLogsUrl) {
            const token = getIdToken();

            const event = {
                player_id: window.pu_environment.principal_id,
                session_id: sessionId,
                log_type: 'Events',
                event_type: 'webgl_load_failed',
                time_stamp_unix: Math.floor(Date.now() / 1000),
                data: {
                    message: err.toString(),
                    stack_trace: err.stack?.toString()
                }
            };

            const records = {
                records: [JSON.stringify(event)],
            };

            const headers = new Headers();
            headers.append('Authorization', token);
            headers.append('Content-Type', 'application/json');

            var frontendLogsPayload = {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(records),
            };

            return fetchRetry(frontedLogsUrl, frontendLogsPayload);
        }
        return Promise.resolve();
    };

    const [initializationFailed, setInitializationFailed] = React.useState<boolean>(false);

    // Runs inside onerror and promise catch callbacks: a monitoring error must not throw there, and a RUM failure must not skip the XB report (or the reverse).
    const failWebGlLoad = (stage: WebGlLoadFailedStage, loaderUrl?: string, err?: unknown) => {
        setInitializationFailed(true);
        try {
            monitoringContext.awsRum?.recordEvent('webgl_load_failed', {
                stage,
                build: buildFromLoaderUrl(loaderUrl),
                message: err === undefined ? undefined : String(err),
            });
        } catch (recordFailure) {
            console.error('Failed to record webgl_load_failed in RUM', { recordFailure });
        }
        // The error screen is shown without a throw, so XbErrorBoundary would count it as a success.
        if (XB_PAGE_FAILURE_STAGES.includes(stage)) {
            try {
                createXbLogger(GAME_XB_COMPONENT).pageFailure();
            } catch (recordFailure) {
                console.error('Failed to record webgl_load_failed in XB', { recordFailure });
            }
        }
    };

    const forceAspectRatio = getEnvConfig().force_aspect_ratio;

    useEffect(() => {
        if (!webGLConfig) {
            return;
        }

        // Game.tsx unmounts this component on auth events (sign-out, token refresh error). A load callback that
        // runs after that is not a game failure, so it must not report one.
        let unmounted = false;
        const failIfMounted = (stage: WebGlLoadFailedStage, loaderUrl?: string, err?: unknown) => {
            if (!unmounted) {
                failWebGlLoad(stage, loaderUrl, err);
            }
        };

        const token = getIdToken();

        const headers = new Headers();
        headers.append('Authorization', token);

        var myInit = {
            method: 'GET',
            headers: headers,
        };

        // Set only for a server-side cause: an error status, or a parsed body that is not valid. A fetch that
        // rejects (network error, timeout) or a body that fails to download or parse stays config_network.
        let configServerFault = false;
        fetchRetry(webGLConfig, myInit)
            .then((resp) => {
                if (!resp.ok) {
                    configServerFault = true;
                    throw new Error(`WebGL config request failed: ${resp.status}`);
                }
                return resp.json();
            })
            .then((data) => {
                configServerFault = true;
                if (!data?.loaderUrl) {
                    throw new Error('WebGL config has no loaderUrl');
                }
                notifyLoadEvent();

                const myLoaderScriptElement = document.createElement('script');
                myLoaderScriptElement.src = data.loaderUrl;
                myLoaderScriptElement.id = 'myLoaderScript';
                myLoaderScriptElement.onload = () => {
                    // Check if Unity 6 loader is available first
                    if (window.MyUnity && typeof window.MyUnity.load === 'function') {
                        // Unity 6 loader
                        const canvas = document.querySelector('#unity-canvas') as HTMLCanvasElement;
                        if (!canvas) {
                            console.error('Canvas not found');
                            failIfMounted('canvas_missing', data.loaderUrl);
                            return;
                        }

                        const unityLoaderUrl = data.loaderUrl?.includes('MyLoader.js')
                            ? data.loaderUrl.replace(/MyLoader\.js$/, 'Build/WebGL.loader.js')
                            : data.loaderUrl;

                        window.MyUnity.load({
                            canvas,
                            loaderUrl: unityLoaderUrl,
                            dataUrl: data.dataUrl,
                            frameworkUrl: data.frameworkUrl,
                            codeUrl: data.codeUrl,
                            streamingAssetsUrl: data.streamingAssetsUrl,
                            symbolsUrl: data.symbolsUrl,
                            companyName: 'Amazon',
                            productName: 'Project Universe',
                            productVersion: data.productVersion,
                            devicePixelRatio:
                                typeof window.computeUnityStartupDevicePixelRatio === 'function'
                                    ? window.computeUnityStartupDevicePixelRatio({
                                          originalDevicePixelRatio: window.devicePixelRatio || 1,
                                          currentBrowserDevicePixelRatio: window.devicePixelRatio || 1,
                                          systemInfo: {},
                                          canvas,
                                      })
                                    : 1,
                            matchWebGLToCanvasSize: false,
                        }).catch((err: any) => {
                            console.log(err);
                            failIfMounted('unity_load', data.loaderUrl, err);
                        });
                    } else if (typeof window.createUnityInstance === 'function') {
                        // Legacy Unity loader
                        window.createUnityInstance(document.querySelector('#unity-canvas'), {
                            dataUrl: data.dataUrl,
                            frameworkUrl: data.frameworkUrl,
                            codeUrl: data.codeUrl,
                            streamingAssetsUrl: data.streamingAssetsUrl,
                            companyName: 'Amazon',
                            productName: 'Project Universe',
                            productVersion: data.productVersion,
                        })
                            .then((unityInstance: any) => {
                                window.myGameInstance = unityInstance;
                                window.devicePixelRatio = 1;
                            })
                            .catch((err: any) => {
                                console.log(err);
                                failIfMounted('unity_load', data.loaderUrl, err);
                            });
                    } else {
                        console.error('No Unity loader found');
                        failIfMounted('no_unity_loader', data.loaderUrl);
                    }
                };
                myLoaderScriptElement.onerror = () => {
                    console.error('Failed to load ' + data.loaderUrl);
                    failIfMounted('loader', data.loaderUrl);
                };
                document.body.appendChild(myLoaderScriptElement);
            })
            .catch((err) => {
                console.log(err);
                if (unmounted) {
                    return;
                }
                failWebGlLoad(configServerFault ? 'config' : 'config_network', undefined, err);
                notifyWebGlFailedLoadEvent(err);
            });

        return () => {
            unmounted = true;
        };
    }, [webGLConfig]);

    if (!webGLConfig) {
        return (
            <div className="cq-content">
                <p>No WebGL build provided.</p>
            </div>
        );
    }

    return initializationFailed ? (
        <div className="cq-content">
            <p>An error has occurred.</p>
            <p>{getBrowserErrorMessage()}</p>
        </div>
    ) : (
        <div className={forceAspectRatio ? 'webgl-wrapper' : 'webgl-wrapper-no-ratio'}>
            <div className="aspect"></div>
            <div className="webgl-content">
                <div id="unityContainer">
                    <canvas id="unity-canvas" style={{ background: '#000000' }}></canvas>
                </div>
            </div>
        </div>
    );
};

export default WebGL;
